import { useEffect, useState, useRef, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';

import { Loader } from '../../../components/StyledComponents';
import { Text } from '../../../shared/uiComponents';
import { formatClientName, getInitials } from '../../../shared/Helpers/functions';
import { DispatchProperties, useSelector } from '../../../redux/store';
import { useDispatch } from 'react-redux';
import {
  getCsDashboard,
  getCsFlaggedSessions,
  CsBcbaCard,
  CsClientDetail,
  CsAssessmentPipelineItem,
  CsClientInactivityItem,
  CsFlaggedSession,
} from '../../../redux/State/clientSlice/dashboardSlice';
import { setClientId, setSearchStringClient } from '../../../redux/State/clientSlice/billingSlice';
import { DASH_COLORS } from '../helpers';

const BCBA_PAGE_SIZE = 8;

const SECTION_AVATAR_STYLES = {
  purple: { background: '#e4ddf5', color: '#7b6bae' },
  teal: { background: '#e8f4f5', color: '#7bbcc0' },
  orange: { background: '#fdf3e8', color: '#d4a054' },
  red: { background: '#fce8e8', color: '#c0392b' },
};

const RatioBadge = ({ pct, color }: { pct: number; color: string }) => {
  const bg = color + '29';
  return (
    <span className="csRatioBadge" style={{ color, background: bg }}>
      {pct.toFixed(1)}%
    </span>
  );
};

const AssessmentStatusBadge = ({ status }: { status: string }) => {
  const map: Record<string, { label: string; className: string }> = {
    NeedsSetup: { label: 'Needs Setup', className: 'csStatusNeedsSetup' },
    InProgress: { label: 'In Progress', className: 'csStatusInProgress' },
    ReadyForReview: { label: 'Ready for Review', className: 'csStatusReady' },
  };
  const { label, className } = map[status] ?? { label: status, className: '' };
  return <span className={`csAssessmentBadge ${className}`}>{label}</span>;
};

const ClientInactivityRow = ({
  item,
}: {
  item: CsClientInactivityItem;
}) => {
  const age = item.client.dateOfBirthday ? getAge(item.client.dateOfBirthday) : null;
  const nameDisplay = age !== null ? `${formatClientName(item.client.fullName, item.client.isElc)}, Age ${age}` : formatClientName(item.client.fullName, item.client.isElc);

  const subtitleParts: string[] = [];
  subtitleParts.push(`BCBA: ${item.bcbaName}`);
  if (item.lastSessionDate) {
    subtitleParts.push(`Last session: ${new Date(item.lastSessionDate).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}`);
  }
  if (item.authEndDate) {
    subtitleParts.push(`Auth active until ${new Date(item.authEndDate).toLocaleDateString('en-US', { month: 'numeric', day: 'numeric', year: '2-digit' })}`);
  }

  return (
    <div className="csInactivityCard csInactivityCardAccent">
      <div className="csInactivityCardLeft">
        <div className="csClientAvatar" style={SECTION_AVATAR_STYLES.orange}>
          {getInitials(item.client.fullName)}
        </div>
        <div className="csInactivityInfo">
          <span className="csInactivityName">{nameDisplay}</span>
          <span className="csInactivitySub">{subtitleParts.join(' · ')}</span>
        </div>
      </div>
      <span className="csInactivityWeeks">{item.weeksInactive} weeks</span>
    </div>
  );
};

const PIPELINE_STATUS_MAP: Record<string, { label: string; color: string; btnLabel: string }> = {
  NeedsSetup: { label: 'Needs Setup', color: '#55aeb3', btnLabel: 'Prepare' },
  InProgress: { label: 'In Progress', color: '#e0944a', btnLabel: 'Continue' },
  ReadyForReview: { label: 'Ready for Review', color: '#5aab72', btnLabel: 'Review' },
};

const AssessmentPipelineRow = ({
  item,
}: {
  item: CsAssessmentPipelineItem;
}) => {
  const navigate = useNavigate();

  const handleAction = () => {
    if (item.status === 'NeedsSetup') {
      navigate(`/clients/client/${item.client.id}`);
    } else if (item.status === 'ReadyForReview' && item.reportId && item.sessionId) {
      navigate(`/soap-notes/info?clientId=${item.client.id}&reportId=${item.reportId}&sessionId=${item.sessionId}`);
    } else if (item.reportId && item.sessionId) {
      navigate(`/soap-notes/edit?clientId=${item.client.id}&reportId=${item.reportId}&sessionId=${item.sessionId}`);
    } else {
      navigate(`/clients/client/${item.client.id}`);
    }
  };

  const statusInfo = PIPELINE_STATUS_MAP[item.status] ?? { label: item.status, color: '#6f757d', btnLabel: 'View' };
  const authStart = new Date(item.authStartDate).toLocaleDateString('en-US', { month: 'numeric', day: 'numeric', year: '2-digit' });
  const authEnd = new Date(item.authEndDate).toLocaleDateString('en-US', { month: 'numeric', day: 'numeric', year: '2-digit' });
  const dueDate = new Date(item.authEndDate).toLocaleDateString('en-US', { month: 'short', day: 'numeric' });

  return (
    <div className="csPipelineRow">
      <div className="csPipelineRowLeft">
        <div className="csClientAvatar" style={SECTION_AVATAR_STYLES.teal}>
          {getInitials(item.client.fullName)}
        </div>
        <div className="csPipelineInfo">
          <span className="csPipelineName">{formatClientName(item.client.fullName, item.client.isElc)} — Reauthorization</span>
          <span className="csPipelineSubtitle">BCBA: {item.bcbaName} · Auth: {authStart} – {authEnd}</span>
        </div>
      </div>
      <div className="csPipelineRight">
        <span className="csPipelineDate">{dueDate}</span>
        <span className="csPipelineStatus" style={{ color: statusInfo.color }}>{statusInfo.label}</span>
        <button className="csClientViewBtn" onClick={handleAction}>{statusInfo.btnLabel}</button>
      </div>
    </div>
  );
};

const getAge = (dob: string) => {
  const birth = new Date(dob);
  const today = new Date();
  let age = today.getFullYear() - birth.getFullYear();
  const m = today.getMonth() - birth.getMonth();
  if (m < 0 || (m === 0 && today.getDate() < birth.getDate())) age--;
  return age;
};

const ClientDetailRow = ({ detail }: { detail: CsClientDetail }) => {
  const navigate = useNavigate();
  const dispatch = useDispatch<DispatchProperties>();

  const handleSoapNotes = () => {
    dispatch(setClientId(detail.client.id));
    dispatch(setSearchStringClient(detail.client.fullName));
    navigate('/soap-notes');
  };

  const age = detail.client.dateOfBirthday ? getAge(detail.client.dateOfBirthday) : null;
  const nameDisplay = age !== null ? `${formatClientName(detail.client.fullName, detail.client.isElc)}, Age ${age}` : formatClientName(detail.client.fullName, detail.client.isElc);

  const subtitleParts: string[] = [];
  subtitleParts.push(`${detail.activeTargetCount} ${detail.activeTargetCount === 1 ? 'target' : 'active targets'}`);

  return (
    <div className="csClientDetailRow">
      <div className="csClientDetailLeft">
        <div className="csClientAvatar" style={SECTION_AVATAR_STYLES.teal}>
          {getInitials(detail.client.fullName)}
        </div>
        <div className="csClientDetailInfo">
          <span className="csClientName">{nameDisplay}</span>
          <span className="csClientSubtitle">
            {subtitleParts[0]}
            {detail.isReauthDue && <span className="csReauthInline"> · Reauth due!</span>}
            {detail.weeksInactive && !detail.isReauthDue && (
              <span className="csInactiveInline"> · {detail.weeksInactive} wks inactive</span>
            )}
          </span>
        </div>
      </div>
      <div className="csClientDetailRight">
        <span className="csClientHours">{detail.btHoursDelivered} / {detail.btHoursAuthorized} hrs</span>
        <span className="csClientRatio" style={{ color: DASH_COLORS.purple, background: `${DASH_COLORS.purple}1a` }}>
          <span className="csRatioDot" style={{ background: DASH_COLORS.purple }} />
          {detail.supervisionRatioPct.toFixed(0)}%
        </span>
        <span className="csClientRatio" style={{ color: DASH_COLORS.indigo, background: `${DASH_COLORS.indigo}1a` }}>
          <span className="csRatioDot" style={{ background: DASH_COLORS.indigo }} />
          {detail.parentTrainingRatioPct.toFixed(0)}%
        </span>
        <button className="csClientSoapLink" onClick={handleSoapNotes}>SOAP Notes</button>
        <button className="csClientViewBtn" onClick={() => navigate(`/clients/client/${detail.client.id}`)}>View</button>
      </div>
    </div>
  );
};

const BcbaCard = ({ bcba }: { bcba: CsBcbaCard }) => {
  const [expanded, setExpanded] = useState(false);

  return (
    <div className="csBcbaCard csBcbaCardAccent">
      <div className="csBcbaCardHeader" onClick={() => setExpanded((p) => !p)}>
        <div className="csBcbaCardLeft">
          <div className="csBcbaAvatar" style={SECTION_AVATAR_STYLES.purple}>
            {getInitials(bcba.fullName)}
          </div>
          <div className="csBcbaInfo">
            <Text className="csBcbaName" title={`${bcba.fullName}, BCBA`} type="psm" fontWeight="semiBold" />
            <span className="csBcbaSubtitle">
              {bcba.clientCount} client{bcba.clientCount !== 1 ? 's' : ''}
            </span>
          </div>
        </div>
        <div className="csBcbaCardRight">
          <div className="csBcbaRatios">
            <span className="csRatioPill" style={{ color: DASH_COLORS.purple, background: `${DASH_COLORS.purple}33` }}>
              <span className="csRatioDot" style={{ background: DASH_COLORS.purple }} />
              97155: {bcba.supervisionRatioPct.toFixed(0)}%
            </span>
            <span className="csRatioPill" style={{ color: DASH_COLORS.indigo, background: `${DASH_COLORS.indigo}33` }}>
              <span className="csRatioDot" style={{ background: DASH_COLORS.indigo }} />
              97156: {bcba.parentTrainingRatioPct.toFixed(0)}%
            </span>
          </div>
          <span className="csAssessmentPill csAssessmentPillNeutral">
            <span className="csRatioDot" style={{ background: bcba.upcomingAssessmentCount > 0 ? '#e0944a' : '#3fba5e' }} />
            {bcba.upcomingAssessmentCount} assess.
          </span>
          <svg
            className={`csBcbaChevron${expanded ? ' csBcbaChevronOpen' : ''}`}
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
          >
            <polyline points="6 9 12 15 18 9" />
          </svg>
        </div>
      </div>
      {expanded && (
        <div className="csBcbaCardBody">
          {bcba.clients.length === 0 ? (
            <div className="csEmptyState">No active clients</div>
          ) : (
            <>
              <div className="csClientDetailHeader">
                Last reviewed: {bcba.clients[0]?.lastReviewedDate
                  ? new Date(bcba.clients[0].lastReviewedDate).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })
                  : '—'} · BT hours (authorized vs. delivered) + BCBA supervision rendered per client
              </div>
              {bcba.clients.map((detail) => (
                <ClientDetailRow key={detail.client.id} detail={detail} />
              ))}
            </>
          )}
        </div>
      )}
    </div>
  );
};

const FLAG_REASON_LABELS: Record<number, string> = {
  0: 'Date conflict',
  1: 'Missing SOAP note',
  2: 'Weekly limit exceeded',
};

const CsFlaggedSessionRow = ({ item }: { item: CsFlaggedSession }) => {
  const navigate = useNavigate();
  const dateStr = new Date(item.date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  const reasonLabel = FLAG_REASON_LABELS[item.flagReason] || 'Flagged';

  const handleResolve = () => {
    if (item.reportId) {
      navigate(`/soap-notes/edit?clientId=${item.clientId}&reportId=${item.reportId}&sessionId=${item.sessionId}`);
    } else {
      navigate(`/clients/client/${item.clientId}`);
    }
  };

  return (
    <div className="csFlaggedRow">
      <div className="csFlaggedRowLeft">
        <div className="csClientAvatar csFlaggedAvatar" style={SECTION_AVATAR_STYLES.red}>
          <svg viewBox="0 0 24 24" fill="currentColor" width="14" height="14">
            <path d="M14.4 6l-.24-1.2c-.09-.46-.5-.8-.98-.8H6c-.55 0-1 .45-1 1v15c0 .55.45 1 1 1s1-.45 1-1v-6h5.6l.24 1.2c.09.47.5.8.98.8H19c.55 0 1-.45 1-1V7c0-.55-.45-1-1-1h-4.6z"/>
          </svg>
        </div>
        <div className="csFlaggedInfo">
          <span className="csFlaggedTitle">{reasonLabel} — {formatClientName(item.clientName, item.isElc)}</span>
          <span className="csFlaggedSub">BT: {item.userName} · {item.sessionTypeName} on {dateStr}</span>
        </div>
      </div>
      <div className="csFlaggedRowRight">
        <span className="csFlaggedDate">{dateStr}</span>
        <button className="csClientViewBtn" onClick={handleResolve}>Resolve</button>
      </div>
    </div>
  );
};

const CSDashboard = () => {
  const dispatch = useDispatch<DispatchProperties>();
  const navigate = useNavigate();
  const loading = useSelector((state) => state.dashboard.loading);
  const csDashboard = useSelector((state) => state.dashboard.csDashboard);
  const csFlaggedSessions = useSelector((state) => state.dashboard.csFlaggedSessions);
  const flaggedLoading = useSelector((state) => state.dashboard.csFlaggedSessionsLoading);
  const error = useSelector((state) => state.dashboard.error);

  const [bcbaSearch, setBcbaSearch] = useState('');
  const [bcbaPage, setBcbaPage] = useState(1);
  const [flaggedPage, setFlaggedPage] = useState(1);
  const flaggedRef = useRef<HTMLDivElement>(null);
  const inactivityRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    dispatch(getCsDashboard());
    dispatch(getCsFlaggedSessions());
  }, [dispatch]);

  const bcbas = csDashboard?.bcbas ?? [];
  const filteredBcbas = useMemo(() =>
    bcbas.filter((b) => b.fullName.toLowerCase().includes(bcbaSearch.toLowerCase())),
    [bcbas, bcbaSearch],
  );
  const pagedBcbas = useMemo(() =>
    filteredBcbas.slice((bcbaPage - 1) * BCBA_PAGE_SIZE, bcbaPage * BCBA_PAGE_SIZE),
    [filteredBcbas, bcbaPage],
  );

  if (loading || flaggedLoading) return <Loader />;

  if (error?.status && !csDashboard) {
    return (
      <div className="btDashboardContainer">
        <div className="btEmptyState">
          <Text title="Unable to load dashboard. Please try again." type="psm" />
        </div>
      </div>
    );
  }

  const summary = csDashboard?.summary;
  const assessmentPipeline = csDashboard?.assessmentPipeline ?? [];
  const clientInactivity = csDashboard?.clientInactivity ?? [];
  const totalBcbaPages = Math.max(1, Math.ceil(filteredBcbas.length / BCBA_PAGE_SIZE));

  return (
    <div className="btDashboardContainer">
      {/* Hero Cards */}
      <div className="btSummaryCards">
        <div className="btSummaryCard btSummaryCardHorizontal btSummaryCardAccentPurple">
          <div className="btSummaryIcon btSummaryIconPurple">
            <svg viewBox="0 0 24 24" fill="currentColor">
              <path d="M16 11c1.66 0 2.99-1.34 2.99-3S17.66 5 16 5c-1.66 0-3 1.34-3 3s1.34 3 3 3zm-8 0c1.66 0 2.99-1.34 2.99-3S9.66 5 8 5C6.34 5 5 6.34 5 8s1.34 3 3 3zm0 2c-2.33 0-7 1.17-7 3.5V19h14v-2.5c0-2.33-4.67-3.5-7-3.5zm8 0c-.29 0-.62.02-.97.05 1.16.84 1.97 1.97 1.97 3.45V19h6v-2.5c0-2.33-4.67-3.5-7-3.5z"/>
            </svg>
          </div>
          <div className="btSummaryCardContent">
            <div className="btSummaryValueRow">
              <Text className="btSummaryValue" title={String(bcbas.length)} type="h3" fontWeight="bold" />
              <span className="btSummaryValueDivider">/ {summary?.clientCount ?? 0}</span>
            </div>
            <Text className="btSummaryLabel" title="BCBAs / Clients" type="psm" fontWeight="semiBold" />
            <Text className="btSummarySubtitle" title="Supervised caseload" type="psm" />
          </div>
        </div>

        <div
          className="btSummaryCard btSummaryCardHorizontal btSummaryCardClickable"
          style={{ borderTopColor: DASH_COLORS.red }}
          onClick={() => flaggedRef.current?.scrollIntoView({ behavior: 'smooth' })}
        >
          <div className="btSummaryIcon btSummaryIconPink">
            <svg viewBox="0 0 24 24" fill="currentColor">
              <path d="M3 13h2v-2H3v2zm0 4h2v-2H3v2zm0-8h2V7H3v2zm4 4h14v-2H7v2zm0 4h14v-2H7v2zM7 7v2h14V7H7z"/>
            </svg>
          </div>
          <div className="btSummaryCardContent">
            <Text className="btSummaryValue btSummaryValueRed" title={String(csFlaggedSessions.length)} type="h3" fontWeight="bold" />
            <Text className="btSummaryLabel" title="Flagged Sessions" type="psm" fontWeight="semiBold" />
            <Text className="btSummarySubtitle" title="Action needed" type="psm" />
          </div>
        </div>

        <div
          className="btSummaryCard btSummaryCardHorizontal btSummaryCardClickable btSummaryCardAccentOrange"
          onClick={() => inactivityRef.current?.scrollIntoView({ behavior: 'smooth' })}
        >
          <div className="btSummaryIcon btSummaryIconAmber">
            <svg viewBox="0 0 24 24" fill="currentColor">
              <path d="M1 21h22L12 2 1 21zm12-3h-2v-2h2v2zm0-4h-2v-4h2v4z"/>
            </svg>
          </div>
          <div className="btSummaryCardContent">
            <Text className="btSummaryValue btSummaryValueOrange" title={String(clientInactivity.length)} type="h3" fontWeight="bold" />
            <Text className="btSummaryLabel" title="Client Inactivity" type="psm" fontWeight="semiBold" />
            <Text className="btSummarySubtitle" title="No services 2+ weeks" type="psm" />
          </div>
        </div>
      </div>

      {/* My BCBAs */}
      <div className="csBcbaSection">
        <div className="btSectionHeader" style={{ marginBottom: 0 }}>
          <div className="btSectionHeaderLeft">
            <div className="csSectionIcon csSectionIconPurple">
              <svg viewBox="0 0 24 24" fill="currentColor">
                <path d="M16 11c1.66 0 2.99-1.34 2.99-3S17.66 5 16 5c-1.66 0-3 1.34-3 3s1.34 3 3 3zm-8 0c1.66 0 2.99-1.34 2.99-3S9.66 5 8 5C6.34 5 5 6.34 5 8s1.34 3 3 3zm0 2c-2.33 0-7 1.17-7 3.5V19h14v-2.5c0-2.33-4.67-3.5-7-3.5zm8 0c-.29 0-.62.02-.97.05 1.16.84 1.97 1.97 1.97 3.45V19h6v-2.5c0-2.33-4.67-3.5-7-3.5z"/>
              </svg>
            </div>
            <Text className="btSectionTitle" title="My BCBAs" type="h6" fontWeight="bold" />
            <span className="csSectionCountText">{filteredBcbas.length} BCBAs</span>
          </div>
          <div className="btSectionHeaderRight">
            <button className="csUtilReportLink" onClick={() => navigate('/reports')}>
              <svg viewBox="0 0 24 24" fill="currentColor" width="16" height="16">
                <path d="M19 3H5c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h14c1.1 0 2-.9 2-2V5c0-1.1-.9-2-2-2zM9 17H7v-7h2v7zm4 0h-2V7h2v10zm4 0h-2v-4h2v4z"/>
              </svg>
              Utilization Report
            </button>
          </div>
        </div>

        <div className="csBcbaToolbar">
          <input
            className="csBcbaSearch"
            placeholder="Search BCBAs..."
            value={bcbaSearch}
            onChange={(e) => { setBcbaSearch(e.target.value); setBcbaPage(1); }}
          />
          {totalBcbaPages > 1 && (
            <div className="csBcbaToolbarRight">
              <span className="csBcbaPageInfo">
                {(bcbaPage - 1) * BCBA_PAGE_SIZE + 1}–{Math.min(bcbaPage * BCBA_PAGE_SIZE, filteredBcbas.length)} of {filteredBcbas.length}
              </span>
              <div className="csPagination csPaginationInline">
                <button
                  className="csPaginationBtn"
                  disabled={bcbaPage === 1}
                  onClick={() => setBcbaPage((p) => Math.max(1, p - 1))}
                >‹</button>
                {Array.from({ length: totalBcbaPages }, (_, i) => i + 1).map((p) => (
                  <button
                    key={p}
                    className={`csPaginationBtn${bcbaPage === p ? ' csPaginationBtnActive' : ''}`}
                    onClick={() => setBcbaPage(p)}
                  >
                    {p}
                  </button>
                ))}
                <button
                  className="csPaginationBtn"
                  disabled={bcbaPage === totalBcbaPages}
                  onClick={() => setBcbaPage((p) => Math.min(totalBcbaPages, p + 1))}
                >›</button>
              </div>
            </div>
          )}
        </div>

        {filteredBcbas.length === 0 ? (
          <div className="btEmptyState">
            <Text title={bcbaSearch ? 'No BCBAs match your search.' : 'No BCBAs assigned.'} type="psm" />
          </div>
        ) : (
          <div className="csBcbaList">
            {pagedBcbas.map((bcba) => (
              <BcbaCard key={bcba.userId} bcba={bcba} />
            ))}
          </div>
        )}
      </div>

      {/* Assessment Pipeline */}
      {assessmentPipeline.length > 0 && (
        <div className="csBcbaSection">
          <div className="btSectionHeader" style={{ marginBottom: 16 }}>
            <div className="btSectionHeaderLeft">
              <div className="csSectionIcon csSectionIconTeal">
                <svg viewBox="0 0 24 24" fill="currentColor">
                  <path d="M19 3h-4.18C14.4 1.84 13.3 1 12 1c-1.3 0-2.4.84-2.82 2H5c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h14c1.1 0 2-.9 2-2V5c0-1.1-.9-2-2-2zm-7 0c.55 0 1 .45 1 1s-.45 1-1 1-1-.45-1-1 .45-1 1-1zm2 14H7v-2h7v2zm3-4H7v-2h10v2zm0-4H7V7h10v2z"/>
                </svg>
              </div>
              <Text className="btSectionTitle" title="Assessment Pipeline" type="h6" fontWeight="bold" />
              <span className="csSectionCountText">{assessmentPipeline.length} upcoming</span>
            </div>
            <div className="btSectionHeaderRight">
              <button className="csUtilReportLink" onClick={() => navigate('/reports')}>
                <svg viewBox="0 0 24 24" fill="currentColor" width="16" height="16">
                  <path d="M19 3H5c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h14c1.1 0 2-.9 2-2V5c0-1.1-.9-2-2-2zM9 17H7v-7h2v7zm4 0h-2V7h2v10zm4 0h-2v-4h2v4z"/>
                </svg>
                Assessment Board
              </button>
            </div>
          </div>
          <div className="csPipelineList">
            {assessmentPipeline.map((item) => (
              <AssessmentPipelineRow key={item.client.id} item={item} />
            ))}
          </div>
        </div>
      )}

      {/* Client Inactivity */}
      {clientInactivity.length > 0 && (
        <div className="csBcbaSection" ref={inactivityRef}>
          <div className="btSectionHeader" style={{ marginBottom: 16 }}>
            <div className="btSectionHeaderLeft">
              <div className="csSectionIcon csSectionIconOrange">
                <svg viewBox="0 0 24 24" fill="currentColor">
                  <path d="M1 21h22L12 2 1 21zm12-3h-2v-2h2v2zm0-4h-2v-4h2v4z"/>
                </svg>
              </div>
              <Text className="btSectionTitle" title="Client Inactivity" type="h6" fontWeight="bold" />
              <span className="csSectionCountText">2+ weeks no service</span>
            </div>
          </div>
          <div className="csInactivityList">
            {clientInactivity.map((item, idx) => (
              <ClientInactivityRow key={`${item.client.id}-${idx}`} item={item} />
            ))}
          </div>
        </div>
      )}

      {/* Flagged Sessions */}
      {csFlaggedSessions.length > 0 && (() => {
        const FLAGGED_PAGE_SIZE = 8;
        const totalFlaggedPages = Math.ceil(csFlaggedSessions.length / FLAGGED_PAGE_SIZE);
        const pagedFlagged = csFlaggedSessions.slice(
          (flaggedPage - 1) * FLAGGED_PAGE_SIZE,
          flaggedPage * FLAGGED_PAGE_SIZE,
        );
        return (
          <div className="csBcbaSection" ref={flaggedRef}>
            <div className="btSectionHeader" style={{ marginBottom: 16 }}>
              <div className="btSectionHeaderLeft">
                <div className="csSectionIcon csSectionIconRed">
                  <svg viewBox="0 0 24 24" fill="currentColor">
                    <path d="M14.4 6l-.24-1.2c-.09-.46-.5-.8-.98-.8H6c-.55 0-1 .45-1 1v15c0 .55.45 1 1 1s1-.45 1-1v-6h5.6l.24 1.2c.09.47.5.8.98.8H19c.55 0 1-.45 1-1V7c0-.55-.45-1-1-1h-4.6z"/>
                  </svg>
                </div>
                <Text className="btSectionTitle" title="Flagged Sessions" type="h6" fontWeight="bold" />
                <span className="csSectionCountText">{csFlaggedSessions.length} need review</span>
              </div>
              {totalFlaggedPages > 1 && (
                <div className="btSectionHeaderRight">
                  <span className="csBcbaPageInfo">
                    {(flaggedPage - 1) * FLAGGED_PAGE_SIZE + 1}–{Math.min(flaggedPage * FLAGGED_PAGE_SIZE, csFlaggedSessions.length)} of {csFlaggedSessions.length}
                  </span>
                  <div className="csPagination csPaginationInline">
                    <button
                      className="csPaginationBtn"
                      disabled={flaggedPage === 1}
                      onClick={() => setFlaggedPage((p) => Math.max(1, p - 1))}
                    >‹</button>
                    {Array.from({ length: totalFlaggedPages }, (_, i) => i + 1).map((p) => (
                      <button
                        key={p}
                        className={`csPaginationBtn${flaggedPage === p ? ' csPaginationBtnActive' : ''}`}
                        onClick={() => setFlaggedPage(p)}
                      >
                        {p}
                      </button>
                    ))}
                    <button
                      className="csPaginationBtn"
                      disabled={flaggedPage === totalFlaggedPages}
                      onClick={() => setFlaggedPage((p) => Math.min(totalFlaggedPages, p + 1))}
                    >›</button>
                  </div>
                </div>
              )}
            </div>
            <div className="csFlaggedList">
              {pagedFlagged.map((fs) => (
                <CsFlaggedSessionRow key={`${fs.sessionId}-${fs.flagReason}`} item={fs} />
              ))}
            </div>
          </div>
        );
      })()}
    </div>
  );
};

export default CSDashboard;
